import { useState } from "react";
import { toast } from "sonner";
import { Check, Gift, Loader2, Mail } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { Eyebrow, Reveal } from "../components/Reveal";

import API from "../api";
const PRESETS = [50, 100, 250, 500];
const PRESET_LINES = {
  50: "A taste of the island — dinner, drinks or the beach sauna",
  100: "A dinner, sauna or a night towards a stay",
  250: "A night for two, well begun",
  500: "A serious dent in an island weekend",
};

const SEO = {
  title: "Gift Vouchers — A Night on Your Own Private Island | Collanmore Island Lodge",
  description:
    "Give a Collanmore Island gift voucher — the gift of an exclusive-use private island in Clew Bay, County Mayo. €100, €250, €500 or your own amount, valid for 5 years, emailed instantly with a unique code.",
  path: "/gift-vouchers/",
};

const euro = (n) => `€${Number(n).toLocaleString("en-IE", { maximumFractionDigits: 2 })}`;

export default function GiftVouchersPage() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [gift, setGift] = useState(false);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", recipient_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const chosen = custom !== "" && Number(custom) > 0 ? Number(custom) : amount;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pickCustom = (v) => {
    setCustom(v);
    if (v !== "" && Number(v) > 0) setAmount(Number(v));
  };

  const buy = async () => {
    setError("");
    if (!form.buyer_name.trim() || !form.buyer_email.trim()) {
      setError("Please add your name and email so we can send the voucher.");
      return;
    }
    if (gift && !form.recipient_email.trim()) {
      setError("Please add the recipient's email address.");
      return;
    }
    if (!(chosen >= 50 && chosen <= 5000)) {
      setError("Vouchers are between €50 and €5,000.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/vouchers/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: chosen, ...form, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not start payment");
      toast.loading("Opening secure checkout…", { duration: 3500 });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message || "Could not start payment — please try again.");
      setBusy(false);
    }
  };

  const inputCls =
    "mt-2 w-full border-b border-navy/25 bg-transparent py-2 text-sm font-light text-navy outline-none focus:border-gold";

  return (
    <PageLayout seo={SEO}>
      <section className="relative overflow-hidden pt-28 md:pt-36">
        <div className="mx-auto max-w-[1100px] px-5 pb-10 md:px-10">
          <Reveal>
            <Eyebrow>Gift Vouchers</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="page-h1">
              Give the gift of your own private island
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-navy/75">
              A Collanmore gift voucher is a night — or a whole island — waiting to happen. Choose an
              amount, add a personal message, and the voucher lands in their inbox within minutes with a
              unique code to quote when they book. Valid for five years.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-4">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <h2 className="font-display text-2xl font-medium text-navy md:text-3xl">Choose an amount</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            {PRESETS.map((p, i) => (
              <Reveal key={p} delay={0.06 * i}>
                <button
                  onClick={() => {
                    setAmount(p);
                    setCustom("");
                  }}
                  data-testid={`voucher-amount-${p}`}
                  className={`w-full border p-6 text-left transition-all duration-300 ${
                    amount === p && !custom
                      ? "border-gold bg-gold/10 shadow-[0_10px_30px_rgba(201,162,39,0.15)]"
                      : "border-navy/15 bg-white/60 hover:border-gold/60"
                  }`}
                >
                  <span className="font-display text-3xl text-navy">{euro(p)}</span>
                  <span className="mt-2 flex items-start gap-2 text-xs font-light text-navy/60">
                    {amount === p && !custom ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> : <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                    {PRESET_LINES[p]}
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.24}>
            <div className="mt-4 max-w-xs">
              <label className="block text-[10px] font-semibold uppercase tracking-widest2 text-navy/55">
                Or your own amount (€50 – €5,000)
              </label>
              <input
                value={custom}
                onChange={(e) => pickCustom(e.target.value.replace(/[^0-9.]/g, ""))}
                inputMode="decimal"
                placeholder="e.g. 150"
                data-testid="voucher-amount-custom"
                className={inputCls}
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 pt-8">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <h2 className="font-display text-2xl font-medium text-navy md:text-3xl">Who is it for?</h2>
          </Reveal>
          <div className="mt-8 grid max-w-3xl grid-cols-1 gap-6">
            <Reveal>
              <label className="flex cursor-pointer items-center gap-3" data-testid="voucher-gift-toggle">
                <input type="checkbox" checked={gift} onChange={(e) => setGift(e.target.checked)} className="h-4 w-4 accent-[#c9a227]" />
                <span className="flex items-center gap-2 text-sm font-light text-navy">
                  <Mail className="h-4 w-4 text-gold" /> Send it straight to the lucky person
                </span>
              </label>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-widest2 text-navy/55">Your name</span>
                  <input value={form.buyer_name} onChange={set("buyer_name")} data-testid="voucher-buyer-name" className={inputCls} />
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-widest2 text-navy/55">Your email</span>
                  <input value={form.buyer_email} onChange={set("buyer_email")} type="email" data-testid="voucher-buyer-email" className={inputCls} />
                </label>
              </div>
            </Reveal>
            {gift && (
              <Reveal delay={0.12}>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-widest2 text-navy/55">Their name</span>
                    <input value={form.recipient_name} onChange={set("recipient_name")} data-testid="voucher-recipient-name" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-widest2 text-navy/55">Their email</span>
                    <input value={form.recipient_email} onChange={set("recipient_email")} type="email" data-testid="voucher-recipient-email" className={inputCls} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-[10px] font-semibold uppercase tracking-widest2 text-navy/55">A personal message (optional)</span>
                    <textarea rows="2" value={form.message} onChange={set("message")} placeholder="Happy birthday — an island is waiting…" data-testid="voucher-message" className={`${inputCls} resize-none`} />
                  </label>
                </div>
              </Reveal>
            )}
          </div>

          {error && (
            <Reveal>
              <p className="mt-6 border border-red-400/40 bg-red-400/10 px-4 py-3 text-sm text-red-700" data-testid="voucher-error">
                {error}
              </p>
            </Reveal>
          )}

          <Reveal delay={0.16}>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <button
                onClick={buy}
                disabled={busy}
                data-testid="voucher-buy-button"
                className="flex items-center gap-3 bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-widest2 text-navy transition-all duration-300 hover:bg-gold-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                {busy ? "Opening secure checkout…" : `Buy voucher — ${euro(chosen || 0)}`}
              </button>
              <p className="text-xs font-light text-navy/60">Secure payment by card · Voucher emailed instantly · Valid 5 years</p>
            </div>
          </Reveal>
        </div>
      </section>
    </PageLayout>
  );
}
